// Copyright (C) 2017-2023 Smart code 203358507

const React = require('react');
const PropTypes = require('prop-types');
const classnames = require('classnames');
const { default: Icon } = require('@stremio/stremio-icons/react');
const { t } = require('i18next');
const { useCore } = require('stremio/core');
const { useProfile, usePlatform, useToast, useBinaryState } = require('stremio/common');
const { Button, Image, Popup } = require('stremio/components');
const ActionButton = require('stremio/components/MetaPreview/ActionButton');
const { default: useRouteFocused } = require('stremio/common/useRouteFocused');
const StreamPlaceholder = require('./StreamPlaceholder');
const styles = require('./styles');
const Stream = ({ className = '', compact = false, isEpg = false, videoId = undefined, videoReleased = undefined, mediaName, mediaYear, quality, video, name, description, thumbnail = undefined, progress, deepLinks, externalPlayerCallbackCanMarkWatched, ...props }) => {
    const profile = useProfile();
    const toast = useToast();
    const platform = usePlatform();
    const core = useCore();
    const routeFocused = useRouteFocused();

    const [menuOpen, openMenu, closeMenu, toggleMenu] = useBinaryState(false);

    const popupLabelOnMouseUp = React.useCallback((event) => {
        if (!event.nativeEvent.togglePopupPrevented) {
            if (event.nativeEvent.ctrlKey || event.nativeEvent.button === 2) {
                event.preventDefault();
                openMenu();
            }
        }
    }, [openMenu]);
    const popupLabelOnContextMenu = React.useCallback((event) => {
        if (!event.nativeEvent.togglePopupPrevented && !event.nativeEvent.ctrlKey && !event.nativeEvent.shiftKey) {
            event.preventDefault();
        }
    }, [toggleMenu]);
    const popupLabelOnLongPress = React.useCallback((event) => {
        if (event.nativeEvent.pointerType !== 'mouse' && !event.nativeEvent.togglePopupPrevented) {
            toggleMenu();
        }
    }, [toggleMenu]);
    const popupMenuOnPointerDown = React.useCallback((event) => {
        event.nativeEvent.togglePopupPrevented = true;
    }, []);
    const popupMenuOnContextMenu = React.useCallback((event) => {
        event.nativeEvent.togglePopupPrevented = true;
        if (!event.nativeEvent.ctrlKey && !event.nativeEvent.shiftKey) {
            event.preventDefault();
        }
    }, []);
    const popupMenuOnClick = React.useCallback((event) => {
        event.nativeEvent.togglePopupPrevented = true;
    }, []);
    const popupMenuOnKeyDown = React.useCallback((event) => {
        event.nativeEvent.buttonClickPrevented = true;
    }, []);

    const href = React.useMemo(() => {
        return deepLinks ?
            deepLinks.externalPlayer ?
                deepLinks.externalPlayer.web ?
                    deepLinks.externalPlayer.web
                    :
                    deepLinks.externalPlayer.openPlayer ?
                        deepLinks.externalPlayer.openPlayer[platform.name] ?
                            deepLinks.externalPlayer.openPlayer[platform.name]
                            :
                            deepLinks.externalPlayer.playlist
                        :
                        deepLinks.player
                :
                deepLinks.player
            :
            null;
    }, [deepLinks]);

    const download = React.useMemo(() => {
        return href === deepLinks?.externalPlayer?.playlist ?
            deepLinks.externalPlayer.fileName
            :
            null;
    }, [href, deepLinks]);

    const target = React.useMemo(() => {
        return href === deepLinks?.externalPlayer?.web ?
            '_blank'
            :
            null;
    }, [href, deepLinks]);

    const downloadLink = React.useMemo(() => {
        return deepLinks?.externalPlayer?.download;
    }, [deepLinks]);

    const streamLink = React.useMemo(() => {
        return deepLinks?.externalPlayer?.streaming;
    }, [deepLinks]);

    // Core may expose a magnet URI as `download` for torrent streams. It is not
    // an HTTP media response, so use the streaming server URL in that case.
    const downloadCandidates = React.useMemo(() => {
        const candidates = [downloadLink, streamLink].filter((url, index, urls) => {
            if (typeof url !== 'string' || urls.indexOf(url) !== index) {
                return false;
            }
            try {
                return ['http:', 'https:'].includes(new URL(url).protocol);
            } catch (_error) {
                return false;
            }
        });
        if (typeof downloadLink === 'string' && downloadLink.startsWith('magnet:')) {
            candidates.reverse();
        }
        return candidates;
    }, [downloadLink, streamLink]);

    const directDownloadUrl = downloadCandidates[0] || null;

    const downloadFileName = React.useMemo(() => {
        const rawSuppliedFileName = deepLinks?.externalPlayer?.fileName;
        const suppliedFileName = typeof rawSuppliedFileName === 'string' &&
            !/\.m3u8?$/i.test(rawSuppliedFileName.trim()) &&
            rawSuppliedFileName.trim().toLowerCase() !== 'playlist' ?
            rawSuppliedFileName.trim() : null;

        const urlFileName = (() => {
            try {
                const pathname = new URL(directDownloadUrl).pathname;
                const lastSegment = pathname.split('/').pop() || '';
                return decodeURIComponent(lastSegment);
            } catch (_error) {
                return '';
            }
        })();

        const candidateForExt = urlFileName || suppliedFileName || '';
        const extMatch = candidateForExt.match(/\.([a-z0-9]{2,5})(?:$|\?)/i);
        const sourceExtension = extMatch && !/m3u8?/i.test(extMatch[1]) ? `.${extMatch[1]}` : '.mp4';

        const episodePart = typeof video?.season === 'number' && typeof video?.episode === 'number' ?
            `S${String(video.season).padStart(2, '0')}E${String(video.episode).padStart(2, '0')}`
            : '';

        let baseTitle = mediaName || '';
        if (!baseTitle && urlFileName) {
            baseTitle = urlFileName.replace(/\.[a-z0-9]{2,5}$/i, '');
        }
        if (!baseTitle && name) {
            baseTitle = name.replace(/\.[a-z0-9]{2,5}$/i, '');
        }
        if (!baseTitle) {
            baseTitle = 'caroline-video';
        }

        let structuredName = '';
        if (episodePart) {
            if (new RegExp('S\\d+E\\d+', 'i').test(baseTitle)) {
                structuredName = baseTitle;
            } else {
                const epTitle = video?.title && !/^episode\s*\d+/i.test(video.title) && !/^episódio\s*\d+/i.test(video.title) ? ` - ${video.title}` : '';
                structuredName = `${baseTitle} - ${episodePart}${epTitle}`;
            }
        } else if (mediaYear && !baseTitle.includes(String(mediaYear))) {
            structuredName = `${baseTitle} (${mediaYear})`;
        } else {
            structuredName = baseTitle;
        }

        const detectedQuality = quality || (typeof description === 'string' && description.match(/\b(4K|2160p|1080p|720p|480p|8K)\b/i)?.[0]?.toUpperCase());
        if (detectedQuality && !structuredName.toUpperCase().includes(detectedQuality.toUpperCase())) {
            structuredName = `${structuredName} [${detectedQuality}]`;
        }

        const sanitized = structuredName
            .replace(/^.*[\\/]/, '')
            .normalize('NFKC')
            .replace(/[<>:"/\\|?*]/g, '-')
            .split('')
            .map((character) => character.charCodeAt(0) < 32 ? '-' : character)
            .join('')
            .replace(/\s+/g, ' ')
            .replace(/[. ]+$/g, '')
            .trim() || 'caroline-video';

        return `${sanitized}${sourceExtension}`;
    }, [deepLinks, directDownloadUrl, mediaName, mediaYear, quality, name, description, video]);

    const companionDownloadUrl = React.useMemo(() => {
        if (process.env.NODE_ENV === 'production' || !directDownloadUrl) return null;
        return `/caroline-download/file?url=${encodeURIComponent(directDownloadUrl)}&fileName=${encodeURIComponent(downloadFileName)}`;
    }, [directDownloadUrl, downloadFileName]);

    const startDirectDownload = React.useCallback((event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
            event.nativeEvent?.stopImmediatePropagation?.();
        }
        closeMenu();

        if (process.env.NODE_ENV === 'production' || !directDownloadUrl) {
            return;
        }

        const downloadHref = companionDownloadUrl || directDownloadUrl;
        const link = document.createElement('a');
        link.href = downloadHref;
        link.download = downloadFileName;
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        window.setTimeout(() => {
            link.remove();
        }, 1000);

        toast.show({
            type: 'success',
            title: t('CAROLINE_DOWNLOAD_STARTED', { defaultValue: 'O download foi iniciado no navegador.' }),
            timeout: 4000
        });
    }, [directDownloadUrl, companionDownloadUrl, downloadFileName, closeMenu, toast, t]);

    const markVideoAsWatched = React.useCallback(() => {
        if (typeof videoId === 'string') {
            core.transport.dispatch({
                action: 'MetaDetails',
                args: {
                    action: 'MarkVideoAsWatched',
                    args: [{ id: videoId, released: videoReleased }, true]
                }
            });
        }
    }, [videoId, videoReleased]);

    const onClick = React.useCallback((event) => {
        if (event.nativeEvent.togglePopupPrevented) {
            return;
        }

        if (isEpg) closeMenu();

        if (profile.settings.playerType !== null) {
            if (profile.settings.playerType !== 'infuse' || !platform.shell.active || !externalPlayerCallbackCanMarkWatched) {
                markVideoAsWatched();
            }
            toast.show({
                type: 'success',
                title: 'Stream opened in external player',
                timeout: 4000
            });
        }

        if (typeof props.onClick === 'function') {
            props.onClick(event);
        }
    }, [props.onClick, profile.settings.playerType, platform.shell.active, externalPlayerCallbackCanMarkWatched, markVideoAsWatched, isEpg, closeMenu]);

    const renderThumbnailFallback = React.useCallback(() => (
        <Icon className={styles['placeholder-icon']} name={'ic_broken_link'} />
    ), []);

    const renderLabel = React.useMemo(() => function renderLabel({ className, children, onLongPress, ...props }) {
        if (compact) {
            return <ActionButton className={classnames(className, styles['stream-action'])} icon={'play'} label={name} variant={'wide'} href={href} target={target} download={download} onClick={onClick} onLongPress={onLongPress} {...props}>
                {children}
            </ActionButton>;
        }

        return (
            <div className={classnames(className, styles['stream-container'], { 'active': menuOpen })} title={name} {...props}>
                <Button className={styles['stream-play-button']} title={name} href={href} target={target} download={download} onClick={onClick} onLongPress={onLongPress}>
                    <div className={styles['info-container']}>
                        {
                            typeof thumbnail === 'string' && thumbnail.length > 0 ?
                                <div className={styles['thumbnail-container']} title={name}>
                                    <Image
                                        className={styles['thumbnail']}
                                        src={thumbnail}
                                        alt={' '}
                                        renderFallback={renderThumbnailFallback}
                                    />
                                </div>
                                :
                                <div className={styles['option-name-container']} title={name}>
                                    <div className={styles['option-name']}>{name}</div>
                                </div>
                        }
                        {
                            progress !== null && !isNaN(progress) && progress > 0 ?
                                <div className={styles['progress-bar-container']}>
                                    <div className={styles['progress-bar']} style={{ width: `${progress}%` }} />
                                    <div className={styles['progress-bar-background']} />
                                </div>
                                :
                                null
                        }
                    </div>
                    {description ? <div className={styles['description-container']} title={description}>{description}</div> : null}
                    <Icon className={styles['icon']} name={'play'} />
                </Button>
                {!isEpg && process.env.NODE_ENV !== 'production' && directDownloadUrl ?
                    <Button
                        className={styles['download-button']}
                        title={t('CAROLINE_DOWNLOAD_DIRECT', { defaultValue: 'Baixar vídeo diretamente' })}
                        aria-label={t('CAROLINE_DOWNLOAD_DIRECT', { defaultValue: 'Baixar vídeo diretamente' })}
                        href={companionDownloadUrl || directDownloadUrl}
                        download={downloadFileName}
                        onClick={startDirectDownload}
                    >
                        <Icon className={styles['download-icon']} name={'download'} />
                    </Button>
                    : null}
                {children}
            </div>
        );
    }, [compact, thumbnail, progress, name, description, href, target, download, onClick, menuOpen, isEpg, directDownloadUrl, companionDownloadUrl, downloadFileName, startDirectDownload, t]);

    const renderMenu = React.useMemo(() => function renderMenu() {
        return (
            <div className={styles['context-menu-content']} onPointerDown={popupMenuOnPointerDown} onContextMenu={popupMenuOnContextMenu} onClick={popupMenuOnClick} onKeyDown={popupMenuOnKeyDown}>
                {description ? <div className={styles['context-menu-title']}>{description}</div> : null}
                <Button className={styles['context-menu-option-container']} title={t('CTX_PLAY')} href={isEpg ? href : undefined} target={isEpg ? target : undefined} download={isEpg ? download : undefined} onClick={isEpg ? onClick : undefined}>
                    <Icon className={styles['menu-icon']} name={'play'} />
                    <div className={styles['context-menu-option-label']}>{t('CTX_PLAY')}</div>
                </Button>
                {
                    !isEpg && process.env.NODE_ENV !== 'production' && directDownloadUrl &&
                        <Button
                            className={styles['context-menu-option-container']}
                            title={t('CAROLINE_DOWNLOAD_DIRECT', { defaultValue: 'Baixar vídeo diretamente' })}
                            href={companionDownloadUrl || directDownloadUrl}
                            download={downloadFileName}
                            onClick={startDirectDownload}
                        >
                            <Icon className={styles['menu-icon']} name={'download'} />
                            <div className={styles['context-menu-option-label']}>{t('CAROLINE_DOWNLOAD_DIRECT', { defaultValue: 'Baixar vídeo diretamente' })}</div>
                        </Button>
                }
            </div>
        );
    }, [startDirectDownload, onClick, isEpg, href, target, download, directDownloadUrl, companionDownloadUrl, downloadFileName, description, popupMenuOnPointerDown, popupMenuOnContextMenu, popupMenuOnClick, popupMenuOnKeyDown]);

    React.useEffect(() => {
        if (!routeFocused) {
            closeMenu();
        }
    }, [routeFocused]);

    return (
        <Popup
            className={className}
            onMouseUp={popupLabelOnMouseUp}
            onLongPress={popupLabelOnLongPress}
            onContextMenu={popupLabelOnContextMenu}
            open={menuOpen}
            portal={isEpg}
            menuClassName={isEpg ? styles['epg-menu'] : undefined}
            onCloseRequest={closeMenu}
            renderLabel={renderLabel}
            renderMenu={renderMenu}
        />
    );
};

Stream.Placeholder = StreamPlaceholder;

Stream.propTypes = {
    className: PropTypes.string,
    compact: PropTypes.bool,
    isEpg: PropTypes.bool,
    videoId: PropTypes.string,
    videoReleased: PropTypes.instanceOf(Date),
    name: PropTypes.string,
    mediaName: PropTypes.string,
    mediaYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    quality: PropTypes.string,
    video: PropTypes.shape({
        season: PropTypes.number,
        episode: PropTypes.number,
        title: PropTypes.string
    }),
    description: PropTypes.string,
    thumbnail: PropTypes.string,
    progress: PropTypes.number,
    deepLinks: PropTypes.shape({
        player: PropTypes.string,
        externalPlayer: PropTypes.shape({
            download: PropTypes.string,
            magnet: PropTypes.string,
            streaming: PropTypes.string,
            playlist: PropTypes.string,
            fileName: PropTypes.string,
            web: PropTypes.string,
            openPlayer: PropTypes.shape({
                ios: PropTypes.string,
                android: PropTypes.string,
                windows: PropTypes.string,
                macos: PropTypes.string,
                linux: PropTypes.string,
            })
        })
    }),
    externalPlayerCallbackCanMarkWatched: PropTypes.bool,
    onClick: PropTypes.func
};

module.exports = Stream;
